import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { AMAZON_ASSOCIATES_DISCLOSURE } from "@/lib/affiliate-disclosure";

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  // Hiding the link is convenience, not security: every admin endpoint
  // re-checks server-side, and /admin renders not-found without it.
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    fetch("/api/admin/whoami", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { isAdmin: false }))
      .then((data) => setIsAdmin((data as { isAdmin?: boolean }).isAdmin === true))
      .catch(() => setIsAdmin(false));
  }, [user]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center">
          <div className="mr-4 flex">
            <Link to="/" className="mr-6 flex items-center space-x-2">
              <span className="font-bold">Pickwell</span>
            </Link>
            {/* No "Home" link — the Pickwell wordmark to the left already
                goes to "/", and a second identical destination in the nav is
                just noise. */}
            <nav className="flex items-center space-x-6 text-xs font-medium">
              {isAdmin && (
                <Link
                  to="/admin"
                  className="label transition-colors hover:text-foreground/80 text-foreground/60"
                >
                  Admin
                </Link>
              )}
              {user && (
                <Link
                  to="/dashboard"
                  className="label transition-colors hover:text-foreground/80 text-foreground/60"
                >
                  Dashboard
                </Link>
              )}
            </nav>
          </div>
          <div className="flex flex-1 items-center justify-end space-x-2">
            <ThemeToggle />
            {user ? (
              <div className="flex items-center gap-4">
                <Link
                  to="/profile"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {user.name}
                </Link>
                <Link
                  to="/settings"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Settings"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </Link>
                <Button variant="outline" size="sm" onClick={() => logout()}>
                  Logout
                </Button>
              </div>
            ) : (
              <Button asChild variant="default" size="sm">
                <Link to="/login">Login</Link>
              </Button>
            )}
          </div>
        </div>
      </header>
      <main className="container py-6">{children}</main>
      {/* The footer exists for one reason: the Associates Operating Agreement
          §5 requires its disclosure sentence "clearly and prominently … on
          your Site", and a policy nobody can reach is not a disclosure. It is
          unconditional, unlike the paragraph on the results page: that one
          describes *those links* (which a deployment with no tag does not earn
          on, so `isAffiliateActive` gates it), while this one describes the
          *relationship*, which exists for the entity either way. */}
      <footer className="border-t">
        <div className="container flex flex-col gap-3 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>{AMAZON_ASSOCIATES_DISCLOSURE}</p>
          <nav className="flex items-center gap-4">
            <Link to="/privacy" className="transition-colors hover:text-foreground">
              Privacy
            </Link>
            <Link to="/terms" className="transition-colors hover:text-foreground">
              Terms
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
